import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Button,
  TablePagination,
  CircularProgress,
  Typography
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { auditApi } from '../../services/accessApi';
import { format } from 'date-fns';

const actionColors = {
  'EMPLOYEE_CREATED': 'success',
  'EMPLOYEE_UPDATED': 'info',
  'EMPLOYEE_DELETED': 'error',
  'ACCESS_GRANTED': 'success',
  'ACCESS_REVOKED': 'error',
  'ACCESS_UPDATED': 'warning',
  'ROLE_CHANGED': 'warning',
  'LICENSE_ASSIGNED': 'info',
  'LICENSE_REMOVED': 'error',
  'EMPLOYEE_ONBOARDED': 'success',
  'EMPLOYEE_OFFBOARDED': 'error'
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    startDate: '',
    endDate: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadLogs();
  }, [page, rowsPerPage, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await auditApi.getAll({
        ...filters,
        page: page + 1,
        limit: rowsPerPage
      });

      setLogs(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPage(0); // Reset to first page when filtering
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = async () => {
    try {
      const response = await auditApi.export(filters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      alert('Error exporting audit logs');
    }
  };

  const formatActionName = (action) => {
    return action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select
            label="Action"
            value={filters.action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
          >
            <MenuItem value="">All Actions</MenuItem>
            {Object.keys(actionColors).map((action) => (
              <MenuItem key={action} value={action}>
                {formatActionName(action)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="End Date"
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ height: '56px' }}
          >
            Export
          </Button>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>System</TableCell>
              <TableCell>Performed By</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No audit logs found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log._id}>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(log.createdAt), 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {format(new Date(log.createdAt), 'HH:mm:ss')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatActionName(log.action)}
                      color={actionColors[log.action] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {log.employee ? (
                      <div>
                        <Typography variant="body2">{log.employee.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {log.employee.email}
                        </Typography>
                      </div>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    {log.system ? log.system.displayName : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {log.performedBy ? (
                      <div>
                        <Typography variant="body2">{log.performedBy.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {log.performedBy.email}
                        </Typography>
                      </div>
                    ) : (
                      'System'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300 }}>
                      {log.notes || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
}
